/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {MarkType} from 'prosemirror-model';
import {EditorState} from 'prosemirror-state';
import {Command} from 'prosemirror-commands';
import {getSelectionMarks, isMarkActive} from '../../../helpers';
import {applyMark, removeMark} from '../../../commands';
import {Dispatch} from './types';

type Name = 'text_color' | 'text_background_color';
type AttrName = 'color' | 'backgroundColor';

interface ColorAttrs {
    color: string;
}

interface BackgroundColorAttrs {
    backgroundColor: string;
}

class TextColor {
    /**
     * Name.
     */
    name: Name;

    /**
     * Attribute Name.
     */
    attrName: AttrName;

    /**
     * Default constructor.
     * 
     * @param name Name.
     * @param attrName Attribute Name.
     */
    constructor(name: Name, attrName: AttrName = 'color') {
        this.name = name;
        this.attrName = attrName;
    }

    /**
     * Apply command.
     * 
     * @param attrs Color Attribute.
     * @returns Command.
     */
    apply(attrs: ColorAttrs | BackgroundColorAttrs): Command {
        return (state: EditorState, dispatch?: Dispatch): boolean => {
            const {schema, selection, doc} = state;

            const type: MarkType = schema.marks[this.name];
            if (!type) {
                return false;
            }

            const {from, to, empty} = selection;

            if (!empty && (from + 1 === to)) {
                const node = doc.nodeAt(from);
                if (node?.isAtom && !node.isText && node.isLeaf) {
                    // An atomic node (e.g. Image) is selected.
                    return false;
                }
            }

            return applyMark(type, attrs)(state, dispatch);
        };
    }

    /**
     * Is command active.
     * 
     * @param state EditorState.
     * @returns Boolean.
     */
    isActive(state: EditorState): boolean {
        const {schema} = state;
        const type: MarkType = schema.marks[this.name];

        if (!type) {
            return false;
        }

        return isMarkActive(state, type);
    }

    /**
     * Get active colors.
     * 
     * @param state EditorState.
     * @returns Array of colors.
     */
    getActiveColors(state: EditorState): string[] {
        if (!this.isActive(state)) {
            return [];
        }

        const {schema} = state;
        const marks = getSelectionMarks(state);

        const colors = marks
            .filter(mark => mark.type === schema.marks[this.name])
            .map(mark => {
                return mark.attrs[this.attrName];
            })
            .filter(Boolean);

        return colors;
    }

    /**
     * Remove command.
     * 
     * @returns Command.
     */
    remove(): Command {
        return (state: EditorState, dispatch?: Dispatch): boolean => {
            const {schema} = state;

            const type = schema.marks[this.name];
            if (!type) {
                return false;
            }

            return removeMark(type)(state, dispatch);
        };
    }

    /**
     * Does command can execute.
     * 
     * @param state EditorState.
     * @returns Boolean.
     */
    canExecute(state: EditorState): boolean {
        const attrs = this.name === 'text_color' ? {color: ''} : {backgroundColor: ''};
        return this.apply(attrs)(state);
    }
}

export default TextColor;
